import {forwardRef, useEffect, useState} from "react";
import Button from "../common/Button";
import CustomListbox, {Item} from "../common/CustomListbox";
import {registerLocale} from "react-datepicker";
import IconCalender from "../../data/Icons/leon-inmobiliaria-icons-kalender.svg";
import "react-datepicker/dist/react-datepicker.css";
import {NextRouter, useRouter} from "next/router";
import AnreiseSelect from "./AnreiseSelect";
import AbreiseSelect from "./AbreiseSelect";
import PersonenSelect from "./PersonenSelect";
import MainFilter from "./MainFilter";

import {enforceArray, handleChecklistFilterChange} from "../../helpers/FilterFunctions";
import {ca, de, es, fr, nl, pl, ru} from "date-fns/locale";
import {useTranslation} from "next-i18next";
import IdSearchField from "../common/IdSearchField";
import {Filter} from "../../data/Filter/Filter.config";
import {useQuery} from "@tanstack/react-query";
import {fetchItem} from "../../data/ReactQueries";
import {cities} from "../../models/cities";

interface ImmobilieMietenProps {
}

registerLocale("de", de);
registerLocale("fr", fr);
registerLocale("nl", nl);
registerLocale("ru", ru);
registerLocale("es", es);
registerLocale("ca", ca);
registerLocale("pl", pl);

export const ExampleCustomInput = forwardRef(
	({value, onClick}: any, ref): any => {
		const [date, setDate] = useState();
		useEffect(() => {
			setDate(value.toString().split("-")[0]);
		}, [value]);

		return (
			<button
				type="button"
				className="w-full border-none shadow-md flex justify-between items-center p-2"
				onClick={onClick}
				ref={ref as any}
			>
				{date}
				<IconCalender className="w-5"/>
			</button>
		);
	}
);
ExampleCustomInput.displayName = "ExampleCustomInput";

function ImmobilieMieten(props: ImmobilieMietenProps) {
	const {data: activeCities, isLoading: activeCitiesLoading} = useQuery(
		["activeCities"],
		(): Promise<cities[]> => {
			return fetchItem("cities", {});
		},
		{initialData: []}
	);
	// let defaultAbreiseDate = new Date();
	// defaultAbreiseDate.setDate(defaultAbreiseDate.getDate() + 7);
	// const [startDateAnreise, setStartDateAnreise] = useState(new Date());
	// const [startDateAbreise, setStartDateAbreise] = useState(defaultAbreiseDate);
	const [startDate, setStartDate] = useState<Date | null>();
	const [endDate, setEndDate] = useState<Date | null>();
	const [personCount, setPersonCount] = useState<number>();
	const [selectedCities, setSelectedCities] = useState<string[]>();
	const [selectedAusstattungsItems, setSelectedAusstattungsItems] =
		useState<Item[]>();
	const router = useRouter();
	const [objecIDSearchVal, setObjecIDSearchVal] = useState<string>((router.query.id as string) ?? "");

	const {t} = useTranslation();

	function handlePersonCountChange(personCount: number) {
		if (personCount > 0) {
			setPersonCount(personCount);
		} else {
			setPersonCount(undefined);
		}
	}

	useEffect(() => {
		if (router.isReady) {
			const personCountFromQuery = enforceArray(router.query.min_persons_total);
			if (
				personCountFromQuery &&
				personCountFromQuery.length > 0 &&
				personCountFromQuery[0]
			) {
				setPersonCount(parseInt(personCountFromQuery[0]));
			}
		}
	}, [router.isReady]);

	function handleFilterChange(
		router: NextRouter,
		startDateAnreise?: Date,
		startDateAbreise?: Date,
		personCount?: number,
		searchedObjectID?: string,
		cities?: string[]
	) {
		let queryParams = router.query;
		if (cities) {
			const cityIds = activeCities
				.filter((city) => cities.includes(city.name))
				.map((city) => city.id.toString());
			queryParams = {...queryParams, [Filter.City.queryKey]: cityIds};
		}
		if (startDateAnreise && startDateAbreise) {
			const anreiseUnixStamp = Math.floor(startDateAnreise.getTime() / 1000);
			const abreiseUnixStamp = Math.floor(startDateAbreise.getTime() / 1000);
			queryParams = {
				...queryParams,
				from_date: anreiseUnixStamp.toString(),
				to_date: abreiseUnixStamp.toString()
			};
		}
		if (personCount && personCount > 0) {
			queryParams = {
				...queryParams,
				min_persons_total: personCount.toString()
			};
		} else {
			delete queryParams.min_persons_total;
		}
		if (searchedObjectID) {
			queryParams = {
				...queryParams,
				id: searchedObjectID
			};
		} else {
			delete queryParams.id;
		}

		if (selectedAusstattungsItems) {
			const ausstattungParamsArray = selectedAusstattungsItems.map((item) => {
				const ausstattungParam = handleChecklistFilterChange(
					item.name,
					true,
					router,
					true
				);
				if (ausstattungParam) return ausstattungParam;
			});
			let ausstattungParams = {};
			ausstattungParamsArray.forEach((param) => {
				if (param) {
					const key = Object.keys(param)[0];
					const val = Object.values(param)[0];
					ausstattungParams = {...ausstattungParams, [key]: val};
				}
			});
			queryParams = {...queryParams, ...ausstattungParams};
		}
		router.push({pathname: "/vermietung", query: queryParams});
	}


	const submit = () => {
		console.log("SUBMIT", router.query, objecIDSearchVal);
		handleFilterChange(
			router,
			startDate ?? undefined,
			endDate ?? undefined,
			personCount,
			objecIDSearchVal ?? router.query.id,
			selectedCities
		);
	}

	return (
		<MainFilter>
			<div className="space-y-2 h-20 col-span-1 md:col-span-2 flex flex-col w-auto md:w-auto">
				<span className="sm:text-[20px]">{t("common.checkIn")}</span>
				<div className="flex gap-4">
					<AnreiseSelect
						startDate={startDate}
						endDate={endDate}
						onEndDateChange={setEndDate}
						onStartDateChange={setStartDate}
					/>
					<AbreiseSelect
						startDate={startDate}
						endDate={endDate}
						onEndDateChange={setEndDate}
						onStartDateChange={setStartDate}
					/>
				</div>
			</div>
			<PersonenSelect
				personCount={personCount}
				onChangePersonCount={(personCount) => {
					handlePersonCountChange(personCount);
				}}
				onSubmit={() => submit()}
			/>
			{/* <div className="space-y-2 col-span-2 lg:col-span-1">
        <span className="text-[16px]">
          {t("components.immobilieMieten.facilities")}
        </span>
        <CustomListbox
          items={AusstattungsItems}
          onSelectItems={setSelectedAusstattungsItems}
        />
      </div> */}
			<div className="space-y-2 h-20 md:w-auto pr-[4px] md:pr-0">
        <span className="text-[16px] md:text-[20px]">
          {t("components.immobilieKaufen.city")}:
        </span>
				<CustomListbox
					items={enforceArray(activeCities).map((city) => {
						return {name: city.name};
					})}
					onSelectItems={(items) => {
						setSelectedCities(items.map((i) => i.name));
					}}
				/>
			</div>
			<IdSearchField
				title={t("common.objectidlabel")}
				onChange={(v) => setObjecIDSearchVal(v)}
				parent={"vermietung"}
				onSubmit={() => submit()}
			/>
			<div
				className="col-span-1 md:col-span-2 lg:col-span-1 lg:align-bottom lg:place-self-end cursor-pointer h-20 space-y-2 w-[33%] md:w-full pl-[4px] md:pl-0"

			>
				<span className="text-[16px]">&nbsp;</span>
				<Button
					padding="px-0"
					onClick={() => {
						submit()
					}}
					text={t("components.searchField.search")}
					transparent={false}
					border={false}
					height={"h-[40px]"}
					className="rounded-[10px] font-bold"
					bgColor="bg-gradient-to-b from-[#a8e1f7] to-[#43c4f7] hover:bg-leo-dark-brown hover:from-transparent hover:to-transparent transition-all duration-300"
				/>
			</div>
		</MainFilter>
	);
}

export default ImmobilieMieten;
